import { ArrowRight, BookOpen, Brain, Code, Users, Zap, Award, Bot, Server, Play, TestTube, MessageCircle, CheckCircle, Target, Cpu, Link2, Workflow, Cog, Wrench, Type, Sparkles, ShieldAlert } from 'lucide-react';
import { usePromotional } from '../../contexts/PromotionalContext';
import { buildUdemyCourseUrl } from '../../utils/udemyLinks';
import { getAiCourseImage } from '../../config/courseImages';

interface LearningPathSectionProps {
  id?: string;
}

const LearningPathSection = ({ id }: LearningPathSectionProps) => {
  // Get promotional context for coupon codes
  const { config, isActive } = usePromotional();
  const courses = [
    {
      id: 0,
      title: "ZERO Code AI Tests: Automate Software Tests in Plain English",
      subtitle: "No-Code AI Testing",
      description: "Automate software tests without writing a single line of code using AI. Describe tests in plain English and let AI generate, execute, and maintain them for you.",
      level: "Beginner",
      duration: "10 hours",
      icon: Sparkles,
      color: "from-emerald-500 to-teal-500",
      borderColor: "border-emerald-200 hover:border-emerald-500/50",
      skills: ["Zero Code Testing", "Plain English Tests", "AI Test Generation", "AI Test Execution", "Test Maintenance", "No-Code Automation"],
      udemy: "zero-code-ai-testing"
    },
    {
      id: 1,
      title: "Using Gen AI & AI Agent in Software Automation Testing",
      subtitle: "Foundation Course",
      description: "Start your AI testing journey with fundamentals of GenAI, prompt engineering, AI Agents, MCP server usage, making Selenium and Playwright test automation smarter with AI.",
      level: "Beginner",
      duration: "8 hours",
      icon: Brain,
      color: "from-blue-500 to-cyan-500",
      borderColor: "border-blue-200 hover:border-blue-500/50",
      skills: ["GenAI Fundamentals", "Prompt Engineering", "AI Agents", "MCP Server", "Selenium AI", "Playwright AI"],
      udemy: "generative-ai-in-software-automation-testing"
    },
    {
      id: 2,
      title: "E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval",
      subtitle: "End-to-End AI Application Testing",
      description: "AI-powered applications are reshaping the software landscape — but how do you test them? Build a complete E2E testing strategy for ChatBots, AI Agents, RAG pipelines, and MCP Servers using DeepEval, the leading open-source LLM evaluation framework.",
      level: "Intermediate",
      duration: "14 hours",
      icon: Zap,
      color: "from-teal-500 to-cyan-600",
      borderColor: "border-teal-200 hover:border-teal-500/50",
      skills: ["DeepEval", "E2E AI Testing", "ChatBot Testing", "AI Agent Testing", "RAG Testing", "MCP Server Testing", "LLM Evaluation", "Real-World Projects"],
      udemy: "ai-testing-deepeval-chatbot-rag-agent-mcp"
    },
    {
      id: 3,
      title: "AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS",
      subtitle: "Testing AI Applications", 
      description: "Learn to test AI applications using specialized frameworks like DeepEval, RAGAs, HuggingFace Evaluate, Confident AI along with using Ollama with Local LLMs for comprehensive AI validation. Testing AI Agents, MCP Server, ChatBots, LLMs.",
      level: "Intermediate",
      duration: "12 hours",
      icon: Code,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-200 hover:border-green-500/50",
      skills: ["DeepEval Framework", "RAGAs Testing", "HuggingFace Evaluate", "Confident AI", "Ollama & Local LLMs", "AI Agents Testing", "MCP Server Testing", "ChatBot Testing"],
      udemy: "ai-testing-deepeval-ragas-ollama"
    },
    {
      id: 4,
      title: "LangChain for Beginners : Building AI Agents, RAG & ChatBots",
      subtitle: "AI System Development",
      description: "Master building and testing AI agents, chatbots, and RAG systems using AutoGen, LangChain with Ollama and local LLMs. See how you can empower existing test automation workflow with Modern AI Agents and simplify automation in every aspect.",
      level: "Intermediate",
      duration: "15 hours",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      borderColor: "border-purple-200 hover:border-purple-500/50",
      skills: ["AI Agent Development", "AutoGen Framework", "LangChain Integration", "ChatBot Testing", "RAG Systems", "Ollama & Local LLMs", "Test Automation Enhancement", "Modern AI Workflows"],
      udemy: "build-ai-agent-chatbot-rag-langchain-local-llm"
    },
    {
      id: 5,
      title: "Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents",
      subtitle: "Practical AI Security Testing",
      description: "Learn to find and fix vulnerabilities in RAG applications, chatbots, and AI agents with Promptfoo. Run practical attacks for prompt injection, jailbreaks, PII leakage, hallucination, excessive agency, policy violations, and knowledge-base poisoning.",
      level: "Beginner",
      duration: "8 hours",
      icon: ShieldAlert,
      color: "from-red-500 to-rose-600",
      borderColor: "border-red-200 hover:border-red-500/50",
      skills: ["Promptfoo", "AI Red Teaming", "OWASP for LLM Apps", "Jailbreak Testing", "Prompt Injection", "RAG Security", "PII Leakage", "Security Reports"],
      udemy: "ai-red-teaming-promptfoo-rag-chatbots-agents"
    },
    {
      id: 6,
      title: "Build Agentic AI & Multi-Agent System for Software Testing",
      subtitle: "Advanced Multi-Agent",
      description: "Learn Building and Testing AI Agent, ChatBot, RAG with LangChain and LangSmith using Ollama and Local LLMs and RAGAs.",
      level: "Advanced",
      duration: "18 hours",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      borderColor: "border-orange-200 hover:border-orange-500/50",
      skills: ["LangChain", "LangSmith", "MCP Server", "ChatBot", "AI Agents", "RAG"],
      udemy: "genai-multi-agent-software-testing"
    },
    {
      id: 7,
      title: "AI-Driven Test Automation: Playwright, Selenium, LLMs & More",
      subtitle: "Intelligent Self-Healing Test Frameworks",
      description: "Build intelligent, resilient test automation frameworks that leverage local LLMs to reduce maintenance, improve test stability, and accelerate bug detection with AI-powered self-healing locators for Selenium & Playwright.",
      level: "Intermediate",
      duration: "15 hours",
      icon: Code,
      color: "from-indigo-500 to-blue-500",
      borderColor: "border-indigo-200 hover:border-indigo-500/50",
      skills: ["Self-Healing Locators", "AI-Powered Selenium", "AI-Powered Playwright", "Local LLMs with Ollama", "Prompt Engineering", "Visual Testing with Vision Models", "Locator Strategy", "Zero Flaky Tests"],
      udemy: "ai-test-automation-selenium-playwright-llms"
    },
    {
      id: 8,
      title: "LLM Fine-Tuning for Beginners: HuggingFace & Unsloth",
      subtitle: "Advanced Specialization",
      description: "Understand Transformer models and learn how to Test, Evaluate, and Train them with HuggingFace NLP library.",
      level: "Expert",
      duration: "20 hours",
      icon: Award,
      color: "from-violet-500 to-purple-600",
      borderColor: "border-violet-200 hover:border-violet-500/50",
      skills: ["HuggingFace", "NLP", "Model Fine-tuning", "Custom AI Model", "Agentic AI"],
      udemy: "ai-with-huggingface"
    }
  ];

  return (
    <section id={id} className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Individual <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Course Details</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore each course in detail. You can purchase individually or save money with our career-focused bundles above.
          </p>
        </div>

        {/* Learning Path Flow */}
        <div className="space-y-8">
          {courses.map((course, index) => {
            const Icon = course.icon;
            const isLast = index === courses.length - 1;
            
            return (
              <div key={course.id} className="relative animate-fade-in" style={{animationDelay: `${index * 200}ms`}}>
                {/* Connector Line */}
                {!isLast && (
                  <div className="absolute left-8 top-20 w-0.5 h-full bg-gradient-to-b from-gray-300 to-transparent"></div>
                )}

                <div className={`bg-white rounded-2xl p-8 border ${course.borderColor} transition-all duration-300 group transform hover:scale-[1.02] shadow-lg hover:shadow-xl`}>
                  <div className="flex items-start space-x-6">
                    {/* Course Number & Icon */}
                    <div className={`p-4 rounded-xl bg-gradient-to-r ${course.color} shadow-lg flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8 text-white" aria-label="Icon icon" />
                    </div>

                    {/* Course Content */}
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                            Course {index + 1}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            course.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                            course.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                            course.level === 'Advanced' ? 'bg-orange-100 text-orange-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {course.level}
                          </span>
                          <span className="text-gray-500 text-sm">{course.duration}</span>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-start gap-4 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h3>
                          <p className="text-lg font-semibold text-green-600">{course.subtitle}</p>
                        </div>
                        <div className="relative w-40 aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-md flex-shrink-0 bg-gray-100">
                          <img
                            src={getAiCourseImage(course.udemy)}
                            alt={`${course.title} course banner`}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className={`absolute right-2 bottom-2 w-8 h-8 rounded-lg bg-gradient-to-r ${course.color} flex items-center justify-center shadow-lg ring-1 ring-white/70`}>
                            <Icon className="w-4 h-4 text-white" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed">{course.description}</p>

                      {/* Skills Grid */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Skills You'll Learn:</h4>
                        <div className="flex flex-wrap gap-2">
                          {course.skills.map((skill, skillIndex) => {
                            // Get icon for specific skills
                            let SkillIcon = null;
                            if (skill.includes('AI Agents')) SkillIcon = Bot;
                            else if (skill.includes('MCP Server')) SkillIcon = Server;
                            else if (skill.includes('Selenium')) SkillIcon = TestTube;
                            else if (skill.includes('Playwright')) SkillIcon = Play;
                            else if (skill.includes('ChatBot') || skill.includes('ChatBots')) SkillIcon = MessageCircle;
                            else if (skill.includes('DeepEval') || skill.includes('RAGAs')) SkillIcon = CheckCircle;
                            else if (skill.includes('HuggingFace')) SkillIcon = Target;
                            else if (skill.includes('NLP')) SkillIcon = Type;
                            else if (skill.includes('Agentic AI')) SkillIcon = Sparkles;
                            else if (skill.includes('Evaluate')) SkillIcon = Target;
                            else if (skill.includes('Confident AI') || skill.includes('Ollama') || skill.includes('LLMs')) SkillIcon = Cpu;
                            else if (skill.includes('AutoGen') || skill.includes('Framework')) SkillIcon = Cog;
                            else if (skill.includes('LangChain')) SkillIcon = Link2;
                            else if (skill.includes('LangSmith')) SkillIcon = Wrench;
                            else if (skill.includes('Integration')) SkillIcon = Link2;
                            else if (skill.includes('RAG Systems') || skill.includes('Systems') || skill === 'RAG') SkillIcon = Server;
                            else if (skill.includes('Workflow') || skill.includes('Enhancement') || skill.includes('Modern')) SkillIcon = Workflow;
                            else if (skill.includes('GenAI') || skill.includes('Brain')) SkillIcon = Brain;
                            else if (skill.includes('Engineering') || skill.includes('Code')) SkillIcon = Code;
                            
                            return (
                              <span 
                                key={skillIndex}
                                className="flex items-center gap-1.5 px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700"
                              >
                                {SkillIcon && <SkillIcon className="w-3.5 h-3.5" aria-label="SkillIcon icon" />}
                                {skill}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Action Button */}
                      <a
                        href={buildUdemyCourseUrl(course.udemy, config, isActive)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${course.color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group/btn`}
                      >
                        <BookOpen className="w-5 h-5 mr-2" aria-label="BookOpen icon" />
                        Enroll in Course {index + 1}
                        <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" aria-label="ArrowRight icon" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Why This Path Section */}
        <div className="mt-20 bg-gray-50 rounded-2xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Follow This Learning Path?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Brain className="w-6 h-6 text-white" aria-label="Brain icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Progressive Learning</h4>
              <p className="text-gray-600 text-sm">Each course builds upon the previous one, ensuring solid foundation and advanced mastery</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Code className="w-6 h-6 text-white" aria-label="Code icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Hands-on Practice</h4>
              <p className="text-gray-600 text-sm">Real-world projects and practical implementations in every course</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-6 h-6 text-white" aria-label="Award icon" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Career Advancement</h4>
              <p className="text-gray-600 text-sm">Transform from traditional QA to AI-powered testing professional</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearningPathSection;
